import { DASHBOARD_WIDGET_KINDS } from "../dashboard-widget-values";
import { reportingNumber, reportingValue } from "../../values/decimal-reporting";
import { countNote, DASHBOARD_COPY } from "../dashboard-copy";
import { decimalAdd, decimalCompare, type DecimalValue } from "../../values/decimal";
import type { DashboardWidgetData } from "../dashboard-data";
import { utcDayStartMs, type DashboardDateRange } from "../dashboard";

export type Point = { label: string; value: DecimalValue };
type ChartPoint = { label: string; value: number };
export type Coverage = DashboardWidgetData["coverage"];
export type AreaSummary = Record<string, DashboardWidgetData>;
type DateLike = Date | string;

export const COMPLETE: Coverage = { complete: true };

export function inRange(date: DateLike, range: DashboardDateRange): boolean {
  const time = new Date(date).getTime();
  return time >= utcDayStartMs(range.start) && time < utcDayStartMs(range.endExclusive);
}

export function stat(value: DecimalValue, coverage: Coverage = COMPLETE): DashboardWidgetData {
  return { kind: DASHBOARD_WIDGET_KINDS.stat, value, coverage };
}

function chartPoint(point: Point): ChartPoint[] {
  const value = reportingNumber(point.value);
  return value === null ? [] : [{ ...point, value }];
}

export function bar(points: Point[], coverage: Coverage = COMPLETE): DashboardWidgetData {
  const chartPoints = points.flatMap(chartPoint);
  const omitted = points.length - chartPoints.length;
  if (omitted === 0) return { kind: DASHBOARD_WIDGET_KINDS.bar, points: chartPoints, coverage };
  const note = countNote(DASHBOARD_COPY.notes.chartNumberRange, omitted);
  return { kind: DASHBOARD_WIDGET_KINDS.bar, points: chartPoints, coverage: { ...coverage, ...coverageOf(false, [coverage.note ?? null, note]) } };
}

function byValueThenLabel(left: Point, right: Point): number {
  return decimalCompare(right.value, left.value) || (left.label < right.label ? -1 : left.label > right.label ? 1 : 0);
}

export function byLabel(left: Point, right: Point): number {
  return left.label < right.label ? -1 : left.label > right.label ? 1 : 0;
}

export function sumBy<T>(rows: readonly T[], keyOf: (row: T) => string, valueOf: (row: T) => DecimalValue): Point[] {
  const totals = new Map<string, string>();
  for (const row of rows) totals.set(keyOf(row), decimalAdd(totals.get(keyOf(row)) ?? 0, valueOf(row)));
  return Array.from(totals, ([label, value]) => ({ label, value: reportingValue(value) })).sort(byValueThenLabel);
}

export function countBy<T>(rows: readonly T[], keyOf: (row: T) => string): Point[] {
  return sumBy(rows, keyOf, () => 1);
}

export function labelOrFallback(value: string | null | undefined, fallback: string): string {
  return value?.trim() || fallback;
}

export function coverageOf(complete: boolean, notes: Array<string | null>): Coverage {
  const note = notes.filter(Boolean).join(" ");
  return note ? { complete, note } : { complete };
}
